// @flow strict

import React, { useState, useMemo, useEffect } from 'react';
import { graphql } from 'gatsby';
import { Box, Grid } from '@material-ui/core';

import { useLanguageContext } from '~contexts/language-context';
import { Layout } from '~components/layout/layout';
import { SearchField } from '~components/search/search-field';
import { SearchResult } from '~components/search/search-result';

import { usePostSearch } from '~hooks/post-search';

import type { Category } from '~types/category';
import type { Alternates, PostNode } from '~types/post';
import { createLinkPath } from '~utils/helpers/create-link-path';

import { withApp } from '../app';

import * as styles from './search.module.less';

/**
 * Type of component properties.
 */
type SearchProps = {
  data: {
    allMarkdownRemark: {
      nodes: Array<PostNode>,
      group: Array<{
        totalCount: number,
        fieldValue: string,
      }>,
    },

    allCategoriesJson: {
      nodes: Array<Category>,
    },
  },

  pageContext: {
    alternates: Alternates,
  },
};

/**
 * Template for search page.
 *
 * @param {SearchProps} props - component props.
 *
 * @returns {Node}
 */
const SearchPage = ({ data, pageContext }: SearchProps) => {
  const posts = data.allMarkdownRemark.nodes;
  const categories = data.allCategoriesJson.nodes;

  const tags = useMemo(
    () =>
      data.allMarkdownRemark.group
        .sort((a, b) => a.totalCount - b.totalCount)
        .reverse(),
    [data.allMarkdownRemark.group],
  );

  const { language, loc } = useLanguageContext();

  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<Array<string>>([]);
  const [selectedCategories, setSelectedCategories] = useState<Array<string>>(
    [],
  );

  // call search hook
  const searchResult = usePostSearch(
    posts,
    searchValue,
    selectedCategories,
    selectedTags,
  );

  // handle click on tags
  const toggleSelectedTags = (tagName) => {
    // check if tag is already selected
    if (selectedTags.indexOf(tagName) === -1) {
      // add tag
      setSelectedTags((prev) => [tagName, ...prev]);
    } else {
      // remove tag
      setSelectedTags((prev) => prev.filter((tag) => tag !== tagName));
    }
  };

  // handle click on categories
  const toggleSelectedCategories = (categoryName) => {
    // check if already selected
    if (selectedCategories.indexOf(categoryName) === -1) {
      // add category
      setSelectedCategories((prev) => [categoryName, ...prev]);
    } else {
      // remove category
      setSelectedCategories((prev) =>
        prev.filter((category) => category !== categoryName),
      );
    }
  };

  const seoFields = {
    title: loc('_search_title'),
    description: loc('_search_description'),
    alternates: pageContext.alternates,
    meta: [
      {
        name: 'robots',
        content: 'noindex, nofollow',
      },
    ],
  };

  return (
    <Layout
      seoFields={seoFields}
      alternates={pageContext.alternates}
      subscribeUrl={createLinkPath({
        path: '/blog/#newsletter-signup',
        language,
      })}
    >
      <div className="container">
        <Grid container justifyContent="flex-end" spacing={4}>
          <Grid item md={9} xs={12}>
            <SearchField searchValue={searchValue} onSearch={setSearchValue} />
          </Grid>
        </Grid>
      </div>

      <section className="container">
        <Grid container spacing={4}>
          <Box
            item
            md={3}
            component={Grid}
            display={{ xs: 'none', md: 'block' }}
          >
            <aside className={styles.sidebar}>
              <h3 className={styles.headline}>
                {loc('_search_most_popular_tags')}
              </h3>
              <ul className={styles.tagList}>
                {tags.map((tag) => {
                  return (
                    <li
                      key={tag.fieldValue}
                      onClick={() => toggleSelectedTags(tag.fieldValue)}
                      className={
                        selectedTags.includes(tag.fieldValue)
                          ? styles.active
                          : ''
                      }
                    >
                      {tag.fieldValue} ({tag.totalCount})
                    </li>
                  );
                })}
              </ul>

              <h3 className={styles.headline}>
                {loc('_search_articles_by_category')}
              </h3>
              <ul className={styles.tagList}>
                {categories.map((category) => {
                  return (
                    <li
                      key={category.name}
                      onClick={() => toggleSelectedCategories(category.name)}
                      className={
                        selectedCategories.includes(category.name)
                          ? styles.active
                          : ''
                      }
                    >
                      {category.name}
                    </li>
                  );
                })}
              </ul>
            </aside>
          </Box>

          <Grid component="section" item md={9} xs={12}>
            {searchValue !== '' && (
              <header>
                <h3 className={styles.headline}>
                  {loc('_search_result')}
                  <span>{searchValue}</span>
                </h3>
              </header>
            )}

            {searchResult.map((post) => {
              return <SearchResult post={post} key={post.id} />;
            })}
          </Grid>
        </Grid>
      </section>
    </Layout>
  );
};

export default withApp(SearchPage);

// noinspection JSUnusedGlobalSymbols
export const pageQuery = graphql`
  query SearchQuery($language: String!, $dateFormat: String!) {
    allMarkdownRemark(
      sort: { fields: [frontmatter___date], order: DESC }
      filter: {
        frontmatter: { language: { eq: $language } }
        fields: { draft: { eq: false } }
      }
    ) {
      nodes {
        ...PostPreview
      }
      group(field: frontmatter___tags) {
        totalCount
        fieldValue
      }
    }

    allCategoriesJson(filter: { language: { eq: $language } }) {
      nodes {
        name
        language
        link
      }
    }
  }
`;
